"use client";

/**
 * Ring System Component
 * 
 * Centralized ring animation system.
 * Handles both brand (multi-color) and user (single-color) rings.
 * 
 * Usage:
 * - Brand ring: <RingSystem type="brand" />
 * - User ring: <RingSystem type="user" color="#6366f1" state="idle" />
 */

import { useEffect, useRef } from "react";
import { BRAND_RING_CONFIG, getUserRingConfig, USER_RING_STATES, type RingType, type UserRingState } from "@/lib/rings/config";

interface RingSystemProps {
  type: RingType;
  color?: string; // Required for user rings, optional for chat interface ring
  state?: UserRingState; // For user rings only
  className?: string;
  customConfig?: {
    opacity?: number;
    blur?: number;
    width?: number;
  }; // For customizable chat interface ring
}

export default function RingSystem({ 
  type, 
  color, 
  state = "idle",
  className = "",
  customConfig,
}: RingSystemProps) {
  const ringRef = useRef<HTMLDivElement>(null);
  
  const getConfig = () => {
    if (type === "brand") return BRAND_RING_CONFIG;
    return getUserRingConfig(color || "#6366f1");
  };
  
  const config = getConfig();

  useEffect(() => {
    if (!ringRef.current) return;

    const ring = ringRef.current;
    const isBrand = type === "brand";
    
    if (isBrand) {
      // Brand ring: multi-color continuous gradient animation (landing page)
      const gradientStops = config.colors.map((color, index) => {
        const percentage = (index / (config.colors.length - 1)) * 100;
        return `${color} ${percentage}%`;
      }).join(", ");
      
      ring.style.setProperty("--ring-gradient", `conic-gradient(from 0deg, ${gradientStops})`);
      ring.style.setProperty("--ring-duration", `${config.duration}s`);
      ring.style.setProperty("--ring-opacity", (customConfig?.opacity ?? config.opacity).toString());
      ring.style.setProperty("--ring-blur", `${customConfig?.blur ?? config.blur}px`);
      ring.style.setProperty("--ring-width", `${customConfig?.width ?? config.width}px`);
      ring.classList.add("ring-brand");
      ring.classList.remove("ring-user");
    } else {
      // User ring: single color, state-aware (chat interface)
      const stateConfig = USER_RING_STATES[state];
      const finalColor = customConfig ? (color || config.colors[0]) : config.colors[0];
      ring.style.setProperty("--ring-color", finalColor);
      ring.style.setProperty("--ring-opacity", (customConfig?.opacity ?? stateConfig.opacity).toString());
      ring.style.setProperty("--ring-blur", `${customConfig?.blur ?? config.blur}px`);
      ring.style.setProperty("--ring-width", `${customConfig?.width ?? config.width}px`);
      
      if (stateConfig.pulse) {
        ring.style.setProperty("--ring-pulse-speed", `${stateConfig.pulseSpeed || 1.5}s`);
        ring.classList.add("ring-pulse");
      } else {
        ring.classList.remove("ring-pulse");
      }
      
      ring.classList.add("ring-user");
      ring.classList.remove("ring-brand");
    }
  }, [type, color, state, config, customConfig]);

  const ringClass = type === "brand" ? "ring-brand" : "ring-user";

  return (
    <div 
      ref={ringRef}
      className={`ring-system ${ringClass} ${className}`}
      aria-hidden="true"
    />
  );
}

